import { useMemo } from 'react';
import { Tooltip } from '@heroui/react';
import { IAttendanceResponse } from '@/entities/Attendance';
import { formatPercentOrDash } from '@/shared/lib/utils/formatPercent';
import { IAttendanceSession, transformAttendanceData } from './lib/transformAttendanceData';

const getCellColors = (attendance: number | null) => {
  if (attendance === 1) return { bg: '#CFF7D3', text: '#092016' };
  if (attendance === 0.5) return { bg: '#FFF4B8', text: '#1E1E1E' };
  if (attendance === 0) return { bg: '#FFD5D5', text: '#1E1E1E' };
  return { bg: '#FFFFFF', text: '#8C8C8C' };
};

const formatAttendanceValue = (attendance: number | null) =>
  attendance === null ? '-' : attendance.toFixed(1);

export const MyAttendance = ({ data }: { data: IAttendanceResponse | undefined }) => {
  const transformedData = useMemo(() => transformAttendanceData(data), [data]);

  const renderAttendanceCell = (session: IAttendanceSession) => {
    const colors = getCellColors(session.attendance);

    return (
      <Tooltip
        content={session.title}
        placement="top"
        classNames={{
          content: 'bg-[#211D55] text-white rounded-[4px] px-4 py-3 text-sm font-semibold',
          base: 'before:bg-[#211D55]',
        }}
      >
        <div
          className="flex h-full items-center justify-center px-3 py-2"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          <span className="text-sm font-medium">{formatAttendanceValue(session.attendance)}</span>
        </div>
      </Tooltip>
    );
  };

  if (!transformedData || transformedData.length === 0) {
    return <div className="text-center text-gray-secondary py-8">No attendance data available</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {transformedData.map((termData) => (
        <div
          key={`${termData.courseTitle}-${termData.termNumber}`}
          className="flex min-w-0 flex-col gap-4 rounded-[16px] bg-[#FAFAFA] p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-semibold leading-[120%]">
                {termData.courseTitle} (Term {termData.termNumber})
              </h3>
              <div className="flex items-center gap-8 text-sm leading-[120%] text-[#344054]">
                <span>
                  Sessions:{' '}
                  <span className="font-bold">
                    {termData.attendedSessions}/{termData.totalLessons}
                  </span>
                </span>
                <span>
                  Last Absence <span className="ml-2">{termData.lastTermAbsence || '-'}</span>
                </span>
                <span>
                  Attendance:{' '}
                  <span className="font-bold text-[#1E1E1E]">
                    {formatPercentOrDash(termData.attendancePercent)}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {termData.dateColumns.length > 0 && (
            <div className="w-full min-w-0 overflow-x-auto pb-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#DFE3E8] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#F2F4F7] [&::-webkit-scrollbar]:h-2">
              <table className="min-w-max border-separate border-spacing-0">
                <caption className="sr-only">Attendance by session</caption>
                <thead>
                  <tr>
                    {termData.dateColumns.map((dateColumn) => (
                      <th
                        scope="colgroup"
                        key={dateColumn.date}
                        colSpan={dateColumn.sessions.length}
                        className="h-[44px] min-w-[128px] border-r border-b border-[#EAECF0] bg-[#F4F6F8] px-2 text-center text-sm font-bold text-[#667085]"
                      >
                        {dateColumn.dateFormatted}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {termData.dateColumns.map((dateColumn) =>
                      dateColumn.sessions.map((session) => (
                        <th
                          scope="col"
                          key={`${dateColumn.date}-${session.title}`}
                          className="h-[44px] min-w-[64px] border-r border-b border-[#EAECF0] bg-white px-1 text-center text-sm font-normal text-[#667085]"
                        >
                          {session.time}
                        </th>
                      )),
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {termData.dateColumns.map((dateColumn) =>
                      dateColumn.sessions.map((session) => (
                        <td
                          key={`${dateColumn.date}-${session.title}`}
                          className="h-[44px] min-w-[64px] border-r border-b border-[#EAECF0] p-0 text-center align-middle"
                        >
                          {renderAttendanceCell(session)}
                        </td>
                      )),
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
